import {Suspense, type ReactNode} from 'react'
import {dynamic} from '@github-ui/dynamic'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ComposerAttachmentScopeContext} from '@github-ui/copilot-shared-ui/contexts/ComposerAttachmentScopeContext'

import type {NormalizedCopilotAgentsTaskPayload} from '../types/copilot-agents-task-payload'
import {AgentsWorkbenchLoadingState} from './AgentsWorkbenchLoadingState'
import {useAgentsWorkbench} from './use-agents-workbench'

const DynamicAgentsWorkbenchHost = dynamic(async () => {
  const module = await import('@github-ui/agent-sessions/components/workbench/AgentsWorkbenchHost')
  return {default: module.AgentsWorkbenchHost}
})

export function AgentsTaskWorkbench({
  payload,
  repository,
  children,
  previewUrl,
}: {
  payload: NormalizedCopilotAgentsTaskPayload
  repository?: {repoOwner: string; repoName: string}
  children: ReactNode
  previewUrl?: string
}) {
  const workbench = useAgentsWorkbench(payload, repository)
  const content = workbench.enabled ? (
    <ErrorBoundary
      boundaryName="AgentsWorkbenchHost"
      fallback={<Suspense fallback={<AgentsWorkbenchLoadingState />}>{children}</Suspense>}
    >
      <Suspense fallback={<AgentsWorkbenchLoadingState />}>
        <DynamicAgentsWorkbenchHost
          key={workbench.task.id}
          conversationScope={{surface: 'chat', id: workbench.task.id}}
          environmentKind={workbench.environmentKind}
          previewUrl={previewUrl}
          ownerLogin={workbench.ownerLogin}
          repositoryId={workbench.task.repository?.id > 0 ? workbench.task.repository.id : undefined}
        >
          {children}
        </DynamicAgentsWorkbenchHost>
      </Suspense>
    </ErrorBoundary>
  ) : (
    children
  )

  return (
    <ComposerAttachmentScopeContext
      value={workbench.environmentKind === 'managed-sandbox' ? (workbench.ownerLogin ?? null) : undefined}
    >
      {content}
    </ComposerAttachmentScopeContext>
  )
}
